import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { listMyInvoices } from "@/lib/invoices.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/invoices")({
  head: () => ({ meta: [{ title: "Invoices — The Hospitality Reserve" }] }),
  component: InvoicesPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-sm text-destructive">Could not load invoices: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-8 text-sm">Not found.</div>,
});

function fmtMoney(p: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(p / 100);
}

function InvoicesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["invoices", "mine"],
    queryFn: () => listMyInvoices(),
  });

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader />
      <main className="mx-auto max-w-5xl px-6 py-10">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <p className="text-[11px] tracking-wide-luxury text-muted-foreground">Records</p>
            <h1 className="text-3xl font-light">Invoices</h1>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Generated automatically once a shift is completed and confirmed. Venue invoices
              cover wages plus the platform fee; bartender self-billing invoices are issued by
              The Hospitality Reserve on the contractor's behalf and represent their gross income
              for HMRC purposes.
            </p>
          </div>
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {error && <p className="text-sm text-destructive">Failed to load.</p>}

        {data && data.invoices.length === 0 && (
          <div className="border border-border p-8 text-sm text-muted-foreground">
            No invoices yet. They appear here automatically once a shift is completed.
          </div>
        )}

        {data && data.invoices.length > 0 && (
          <div className="border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-[11px] uppercase tracking-wide-luxury text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left">Number</th>
                  <th className="px-4 py-3 text-left">Issued</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">View</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-border">
                    <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(inv.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-4 py-3 capitalize">{inv.type.replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 text-right">{fmtMoney(inv.total_pence)}</td>
                    <td className="px-4 py-3 text-right">
                      <Link to="/invoices/$invoiceId" params={{ invoiceId: inv.id }}
                        className="text-xs tracking-wide-luxury text-accent hover:text-accent/80">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
